import { LoadingPage } from '@chiroup/components/LoadingPage';
import { ROLE_LABELS } from '@chiroup/core/types/AdminUserRole.type';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import useAdminUsers, { AdminUserRow } from '../../hooks/useAdminUsers';
import List from '../common/list/List';
import ListItem from '../common/list/ListItem';
import Header from '../layout/Header';
import { AdminUserCreateModal } from './AdminUserCreateModal';

const AdminUserList: React.FC = () => {
  const { hasPermission, isLoading: permissionsLoading } = useAdminPermissions();
  const canView = hasPermission('admin_users.view');
  const { data, isFetching } = useAdminUsers(canView);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const items: AdminUserRow[] = (data?.items ?? []).filter(u =>
    [u.email, u.firstName, u.lastName].some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  const AddButton = hasPermission('admin_users.edit') ? (
    <button
      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
      onClick={() => setCreateOpen(true)}
    >
      Add Admin User
    </button>
  ) : undefined;

  if (permissionsLoading) {
    return <LoadingPage />;
  }

  if (!canView) {
    return (
      <>
        <Header title="Admin Users" />
        <div className="p-6 text-sm text-gray-500 dark:text-dark-gray-400">
          You do not have permission to manage admin users.
        </div>
      </>
    );
  }

  return (
    <>
      <Header title="Admin Users" rightSide={AddButton}>
        <div className="mt-4">
          <input
            type="text"
            className="block w-full sm:text-sm border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-gray-800 dark:border-dark-gray-600 dark:text-white px-3 py-2 border"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </Header>
      <List isFetching={isFetching} clientSide>
        {items.map((u, i) => (
          <Link to={`/admin-users/${u.ID}`} key={u.ID}>
            <ListItem index={i}>
              <div className="flex items-center justify-between space-x-4">
                <div className="min-w-0">
                  <div className="relative group flex items-center space-x-4">
                    <svg
                      className="shrink-0 w-10 h-10 text-gray-400 group-hover:text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="2 2 16 16"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="space-y-1">
                      <h2 className="text-sm font-medium leading-4">
                        <span className="absolute inset-0" />
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}
                      </h2>
                      <div className="text-sm leading-4 text-gray-500 group-hover:text-gray-900 truncate">
                        {u.email}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="hidden sm:flex flex-col shrink-0 items-end space-y-1">
                  <p className="text-sm font-medium text-gray-700 dark:text-dark-gray-200">
                    {ROLE_LABELS[u.roleKey] ?? u.roleKey}
                  </p>
                  <div className="flex items-center space-x-3 text-sm text-gray-500">
                    <span
                      className={
                        u.status === 'active'
                          ? 'text-green-600 font-medium'
                          : u.status === 'disabled'
                            ? 'text-red-600 font-medium'
                            : 'text-yellow-600 font-medium'
                      }
                    >
                      {u.status}
                    </span>
                    <span>MFA: {u.mfaEnrolled ? 'Yes' : 'No'}</span>
                    {u.lastLoginAt && <span>Last: {u.lastLoginAt}</span>}
                  </div>
                </div>
                <div className="sm:hidden">
                  <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </ListItem>
          </Link>
        ))}
      </List>
      {createOpen && <AdminUserCreateModal onClose={() => setCreateOpen(false)} />}
    </>
  );
};

export default AdminUserList;
